﻿(function (angular) {
  "use strict";

  angular.module("Toyota.Factories").factory("Range", [
    "$q",
    "$filter",
    "API",
    "RangeVariantApi",
    "RangeTrimApi",
    "RangePaintApi",
    "RangePricesApi",
    "DebugLogger",
    function ($q, $filter, API, RangeVariantApi, RangeTrimApi, RangePaintApi, RangePricesApi, DebugLogger) {
      var cache = {
        range: {
          // all these types are expected to have an ID property
          grade: {},
          variant: {},
          trim: {},
          paint: {},
          pricingOptions: {},
        },
        childIdsByParent: {
          grade: {},
          variant: {},
          trim: {},
          paint: {}, // not really in use but keeps get(Thing)By(Field) methods simpler
        },
        gradesList: [],
        variantOptions: {},
        bodyPaints: [],
        roofPaints: [],
      };

      function serializePricingParams(pricingParams) {
        return pricingParams.postcode + "," + (pricingParams.usageType || "no-usage-type");
      }

      function cacheMaterialCodePricingOptions(materialCode, pricingParams, pricingOptions) {
        if (!pricingParams || !pricingParams.postcode) {
          throw new Error("Setting pricing options requires postcode");
        }

        if (!Object.prototype.hasOwnProperty.call(cache.range.pricingOptions, materialCode)) {
          cache.range.pricingOptions[materialCode] = {};
        }

        cache.range.pricingOptions[materialCode][serializePricingParams(pricingParams)] = pricingOptions;
      }

      function getPricingOptions(materialCode, pricingParams) {
        return hasPricingOptions(materialCode, pricingParams)
          ? cache.range.pricingOptions[materialCode][serializePricingParams(pricingParams)]
          : null;
      }

      function hasPricingOptions(materialCode, pricingParams) {
        var pricingOptions = cache.range.pricingOptions[materialCode];
        return !!(pricingOptions && pricingOptions[serializePricingParams(pricingParams)]);
      }

      function hydratedPaint(paint, optionalPricingParams) {
        var hydratedPaint = angular.copy(paint);

        if (!optionalPricingParams) {
          hydratedPaint.PricingOptions = null;
          return hydratedPaint;
        }

        hydratedPaint.PricingOptions = getPricingOptions(paint.MaterialCode, optionalPricingParams);
        return hydratedPaint;
      }

      function hydratedItem(item, type, optionalPricingParams) {
        if (type === "paint") {
          return hydratedPaint(item, optionalPricingParams);
        }

        return item;
      }

      function hydrateThenCacheItem(item, type, pricingParams) {
        if (type === "paint") {
          cachePaintAsBodyPaintWithRoofOptions(item);
        }

        if (type === "paint" && pricingParams) {
          cacheMaterialCodePricingOptions(item.MaterialCode, pricingParams, item.PricingOptions);
        }

        cache.range[type][item.ID] = hydratedItem(item, type, pricingParams);
      }

      function initChildIdsByParent(parentId, parentType) {
        cache.childIdsByParent[parentType][parentId] = [];
      }

      function mapChildIdUnderParent(parentId, parentType, childId) {
        if (typeof cache.childIdsByParent[parentType][parentId] === "undefined") {
          initChildIdsByParent(parentId, parentType);
        }

        cache.childIdsByParent[parentType][parentId].push(childId);
      }

      function getRawItemId(item, type) {
        /*
         * grades object has all different names for what should be ID -- let's
         * normalise that so we can use more generic tree-traversal logic
         */

        if (type === "grade") {
          return item.gradeId;
        }

        if (type === "variant") {
          return item.id;
        }

        return item.ID;
      }

      function ingestGetRangeResponse(response, id, type, childType, pricingParams) {
        if (!response.success) {
          // prettier-ignore
          var errorText =
            "ingestGetRangeResponse: server responded with falsy success " +
            "(" +
              "id: " + id + ", " +
              "type:" + type + ", " +
              "childType: " + childType + ", " +
              "pricingParams: " + (pricingParams ? "(present)" : "(absent)") +
            ")";
          DebugLogger.log("Range", errorText);
          throw new Error("Range." + errorText);
        }

        var item = response[type];

        if (childType) {
          var childItems = response[childType + "s"];

          if (typeof childItems !== "undefined") {
            initChildIdsByParent(id, type);
            angular.forEach(childItems, function (childItem) {
              childItem.ID = getRawItemId(childItem, childType);
              mapChildIdUnderParent(id, type, childItem.ID);
              hydrateThenCacheItem(childItem, childType, pricingParams);
            });
          }
        }

        item.ID = getRawItemId(item, type);
        hydrateThenCacheItem(item, type, pricingParams);

        return cache.range[type][id];
      }

      function getItemWithChildrenById(id, type, childType, service, pricingParams) {
        /*
         * Service can be anything that implements { getById => Promise }, currently:
         * - RangeVariantApi (implements RangeVariantApi.getById)
         * - RangeTrimApi (implements RangeTrimApi.getById)
         * - RangePaint (implements RangePaintApi.getById)
         */

        if (typeof id === "undefined" || id === null) {
          throw new TypeError("getItemWithChildrenById requires an ID");
        }

        if (typeof type === "undefined" || type === null) {
          throw new TypeError("getItemWithChildrenById requires a type");
        }

        if (typeof service === "undefined" || service === null) {
          throw new TypeError("getItemWithChildrenById requires a service");
        }

        if (
          typeof cache.range[type][id] !== "undefined" &&
          (typeof cache.childIdsByParent[type][id] !== "undefined" || !childType)
        ) {
          return API.getCancellablePromiseFromValue(hydratedItem(cache.range[type][id], type, pricingParams));
        }

        if (!Object.prototype.hasOwnProperty.call(service, "getById")) {
          throw new TypeError("getItemWithChildrenById requires a service with method getById");
        }

        return service
          .getById(id, pricingParams)
          .then(function (response) {
            return ingestGetRangeResponse(response, id, type, childType, pricingParams);
          })
          .catch(function () {
            // prettier-ignore
            var errorText =
              "getItemWithChildrenById: server responded with falsy success " +
              "(" +
                "id: " + id + ", " +
                "type:" + type + ", " +
                "childType: " + childType + ", " +
                "pricingParams: " + (pricingParams ? "(present)" : "(absent)") +
              ")";
            DebugLogger.log("Range", errorText);
            throw new Error("Range." + errorText);
          });
      }

      function getItemsWithChildrenByIds(ids, type, childType, service, pricingParams) {
        return $q.all(
          ids.map(function (id) {
            return getItemWithChildrenById(id, type, childType, service, pricingParams);
          })
        );
      }

      function getPaintByMaterialCodeEx(materialCode, pricingParams, pricingOptionsNotNeeded) {
        var cachedPaintIdsByMaterialCode = Object.keys(cache.range.paint).filter(function (paintId) {
          return cache.range.paint[paintId].MaterialCode === materialCode;
        });

        if (cachedPaintIdsByMaterialCode.length > 0) {
          var cachedPaint = cache.range.paint[cachedPaintIdsByMaterialCode[0]]; // we only expect max 1 match

          /*
           * If we don't need to update PricingOptions because we're ignoring them
           * or because pricingParams hasn't changed, then cache is still valid
           */
          if (hasPricingOptions(materialCode, pricingParams) || pricingOptionsNotNeeded) {
            return API.getCancellablePromiseFromValue(
              hydratedPaint(cachedPaint, pricingOptionsNotNeeded ? null : pricingParams)
            );
          }
        }

        return RangePaintApi.getByMaterialCode(materialCode, pricingParams).then(function (response) {
          return ingestGetRangeResponse(response, response.paint.ID, "paint", "", pricingParams);
        });
      }

      function hydratedItemsByIds(itemIds, type, optionalPricingParams) {
        return itemIds.map(function (itemId) {
          return hydratedItem(cache.range[type][itemId], type, optionalPricingParams);
        });
      }

      function getChildrenByParentId(parentId, type, parentType, service, optionalPricingParams) {
        if (typeof cache.childIdsByParent[parentType][parentId] !== "undefined") {
          return API.getCancellablePromiseFromValue(
            hydratedItemsByIds(cache.childIdsByParent[parentType][parentId], type, optionalPricingParams)
          );
        }

        return getItemWithChildrenById(parentId, parentType, type, service, optionalPricingParams).then(function () {
          return hydratedItemsByIds(cache.childIdsByParent[parentType][parentId], type, optionalPricingParams);
        });
      }

      function flattenGetChildrenPromises(getChildrenPromises) {
        return API.all(getChildrenPromises).then(function (getChildrenResults) {
          return getChildrenResults.reduce(function (flattenedGetChildrenResults, getChildrenResult) {
            return flattenedGetChildrenResults.concat(getChildrenResult);
          }, []);
        });
      }

      function getChildrenByParentIds(parentIds, type, parentType, service, optionalPricingParams) {
        var getChildrenPromises = parentIds.map(function (parentId) {
          return getChildrenByParentId(parentId, type, parentType, service, optionalPricingParams);
        });

        return flattenGetChildrenPromises(getChildrenPromises);
      }

      function getVariantsByGradeIdFromCache(gradeId) {
        return getGradeByIdFromCache(gradeId).variants;
      }

      function getVariantByIdFromCache(variantId) {
        return cache.range.variant[variantId];
      }

      function getPricingOptionsAndSpecialOffersByMaterialCode(materialCode, pricingParams) {
        return getPaintByMaterialCodeEx(materialCode, pricingParams).then(function (response) {
          return {
            pricingOptions: response.PricingOptions,
            specialOffers: response.SpecialOffers,
          };
        });
      }

      function getPricingOptionsByMaterialCodes(materialCodes, pricingParams) {
        var cachedPricingOptionsByMaterialCodes = [];
        var uncachedMaterialCodes = angular.copy(materialCodes);

        angular.forEach(materialCodes, function (materialCode) {
          if (hasPricingOptions(materialCode, pricingParams)) {
            cachedPricingOptionsByMaterialCodes.push({
              pricingOptions: getPricingOptions(materialCode, pricingParams),
            });
            uncachedMaterialCodes = uncachedMaterialCodes.filter(function (uncachedMaterialCode) {
              return uncachedMaterialCode !== materialCode;
            });
          }
        });

        if (!uncachedMaterialCodes.length) {
          return API.getCancellablePromiseFromValue(cachedPricingOptionsByMaterialCodes);
        }

        return RangePricesApi.getByMaterialCodes(
          uncachedMaterialCodes.map(function (materialCode) {
            return {
              materialCode: materialCode,
            };
          }),
          pricingParams.postcode,
          pricingParams.usageType
        )
          .then(function (response) {
            if (!response.success) {
              // prettier-ignore
              var errorText =
                "getPricingOptionsByMaterialCodes: RangePricesApi.getByMaterialCodes -> server responded with falsy success " +
                "(" +
                  "materialCodes: [" + materialCodes.join(", ") + "], " +
                  "pricingParams: " + (pricingParams ? "(present)" : "(absent)") +
                ")";
              DebugLogger.log("Range", errorText);
              throw new Error("Range." + errorText);
            }

            angular.forEach(response.Prices, function (el) {
              cacheMaterialCodePricingOptions(el.MaterialCode, pricingParams, el.PricingOptions);
            });

            var pricingOptionsByMaterialCodes = response.Prices.map(function (el) {
              return {
                pricingOptions: el.PricingOptions,
              };
            });

            return cachedPricingOptionsByMaterialCodes.concat(pricingOptionsByMaterialCodes);
          })
          .catch(function () {
            // prettier-ignore
            var errorText =
              "getPricingOptionsByMaterialCodes: server responded with falsy success " +
              "(" +
                "materialCodes: [" + materialCodes.join(", ") + "], " +
                "pricingParams: " + (pricingParams ? "(present)" : "(absent)") +
              ")";
            DebugLogger.log("Range", errorText);
            throw new Error("Range." + errorText);
          });
      }

      function getPricingOptionsByMaterialCode(materialCode, pricingParams) {
        return getPricingOptionsByMaterialCodes([materialCode], pricingParams).then(function (response) {
          return response[0];
        });
      }

      function ingestGradesList(gradesObject) {
        cache.gradesList = gradesObject;

        gradesObject.forEach(function (gradeObject) {
          var grade = angular.copy(gradeObject);
          ingestGetRangeResponse(
            { success: true, grade: grade, variants: grade.variants },
            getRawItemId(grade, "grade"),
            "grade",
            "variant",
            null
          );
        });
      }

      function getGradeByIdFromCache(id) {
        return $filter("arrayFind")(cache.gradesList, function (grade) {
          return grade.gradeId === id;
        });
      }

      function getParentIdByChildId(id, parentType) {
        var keys = Object.keys(cache.childIdsByParent[parentType]);
        for (var i = 0; i < keys.length; i++) {
          var trimId = cache.childIdsByParent[parentType][keys[i]].find(function (childId) {
            return id === childId;
          });
          if (trimId) {
            return keys[i];
          }
        }

        return null;
      }

      function getGradeIdByPaintId(paintId) {
        return getParentIdByChildId(getParentIdByChildId(getParentIdByChildId(paintId, "trim"), "variant"), "grade");
      }

      function getGradeIdByMaterialCode(materialCode) {
        return getPaintByMaterialCodeEx(materialCode, {}, true)
          .then(function (result) {
            return result;
          })
          .then(function (paint) {
            return getGradeIdByPaintId(paint.ID);
          });
      }

      function getVariantIdByPaintId(paintId) {
        return getParentIdByChildId(getParentIdByChildId(paintId, "trim"), "variant");
      }

      function getVariantByMaterialCode(materialCode) {
        return getPaintByMaterialCodeEx(materialCode, {}, true)
          .then(function (result) {
            return result;
          })
          .then(function (paint) {
            return getTrimIdByPaintId(paint.ID); // getParentIdByChildId(id, 'trim')
          })
          .then(function (trimId) {
            return getVariantIdByTrimId(trimId); // getParentIdByChildId(id, 'variant')
          })
          .then(function (variantId) {
            return getVariantById(variantId); // getItemWithChildrenById(id, 'variant', 'trim', RangeVariantApi)
          })
          .then(function (variant) {
            return variant;
          });
      }

      function getTrimByMaterialCode(materialCode) {
        return getPaintByMaterialCodeEx(materialCode, {}, true).then(function (paint) {
          return getTrimById(getTrimIdByPaintId(paint.ID));
        });
      }

      function getPaintParentVariantNameFromCache(paintId) {
        var paintParentVariantId = getVariantIdByPaintId(paintId);
        return cache.range.variant[paintParentVariantId].name;
      }

      function getPaintParentTrimCodeFromCache(paintId) {
        return getTrimByIdFromCache(getTrimIdByPaintId(paintId)).TrimCode;
      }

      function foundBodyPaintForPaint(bodyPaint, paint) {
        var isFound = true;
        if (Array.isArray(paint.RelatedColourPaintIDs) && paint.RelatedColourPaintIDs.length) {
          isFound = bodyPaint.ID === paint.ID || bodyPaint.ID === paint.RelatedColourPaintIDs[0];
        }
        else {
          isFound = bodyPaint.ID === paint.ID;
        }

        return (
          bodyPaint.BodyPaintDescription === paint.BodyPaintDescription &&
          bodyPaint.ParentTrimCode === getPaintParentTrimCodeFromCache(paint.ID) &&
          isFound
        );
      }

      function foundRoofPaintInFoundBodyPaintRoofOption(foundBodyPaintRoofOption, roofOption, paint) {
        var isFound = true;
        if (Array.isArray(paint.RelatedColourPaintIDs) && paint.RelatedColourPaintIDs.length) {
          isFound = foundBodyPaintRoofOption.ID === paint.ID || foundBodyPaintRoofOption.ID === paint.RelatedColourPaintIDs[0];
        }
        else {
          isFound = foundBodyPaintRoofOption.ID === paint.ID
        }

        return (
          foundBodyPaintRoofOption.RoofPaintDescription === roofOption.RoofPaintDescription &&
          isFound
        );
      }

      function cachePaintAsBodyPaintWithRoofOptions(paint) {
        /*
         * - We cache an MDM paint as a bodyPaint / roofPaint pair for F/E
         *   consumption, deduplicating redundant bodyPaint entries by using
         *   body paint description as a key
         * - Unfortunately when we do this, we end up merging data across
         *   multiple trims and grades
         * - So, we use both body paint description AND parent grade ID as a
         *   key
         */
        var paintParentVariantId = getVariantIdByPaintId(paint.ID);

        // Not ready to cache yet, we absolutely need both of these
        if (!paint.BodyPaintDescription || !paintParentVariantId) {
          return;
        }

        var paintParentTrimCode = getPaintParentTrimCodeFromCache(paint.ID);
        var paintParentVariantName = getPaintParentVariantNameFromCache(paint.ID);

        var foundBodyPaint = $filter("arrayFind")(cache.bodyPaints, function (bodyPaint) {
          return foundBodyPaintForPaint(bodyPaint, paint);
        });

        if (!foundBodyPaint) {
          foundBodyPaint = {
            // Note: BodyPaintId is not a real paint ID you can look up in any API. It's just for the front-end!
            BodyPaintId:
              "body-paint-" +
              (
                (paint.BodyPaintDescription || "") +
                "_" +
                paintParentTrimCode +
                "_" +
                paintParentVariantName
              ).replace(/[^A-Za-z0-9-]+/, "-"),
            ID: paint.ID,
            ParentTrimCode: paintParentTrimCode,
            BodyPaintDescription: paint.BodyPaintDescription,
            BodyPaintSwatchHex: paint.BodyPaintSwatchHex,
            BodyPaintSwatchImage: paint.BodyPaintSwatchImage,
            BodyPaintOrder: paint.BodyPaintOrder,
            RoofPaintOrder: paint.RoofPaintOrder,
            RoofOptions: [],
          };

          cache.bodyPaints.push(foundBodyPaint);
          cache.bodyPaints = cache.bodyPaints.sort(function (a, b) {
            if (a.BodyPaintOrder === b.BodyPaintOrder) {
              return a.RoofPaintOrder - b.RoofPaintOrder;
            }
            return a.BodyPaintOrder > b.BodyPaintOrder ? 1 : -1;
          });
        }

        var roofOption = {
          // But, RoofOption[...].ID is a real paint ID which correlates to the MDM APIs.
          ID: paint.ID,
          ParentTrimCode: paintParentTrimCode,
          MaterialCode: paint.MaterialCode,
          UsageType: paint.UsageType,
          AvailableTrims: paint.AvailableTrims,
          RoofPaintDescription: paint.RoofPaintDescription,
          RoofPaintSwatchHex: paint.RoofPaintSwatchHex,
          RoofPaintSwatchImage: paint.RoofPaintSwatchImage,
          RelatedColourPaintIDs: paint.RelatedColourPaintIDs,
          BodyPaintOrder: paint.BodyPaintOrder,
          RoofPaintOrder: paint.RoofPaintOrder
        };

        if (!roofOption.RoofPaintDescription) {
          roofOption.RoofPaintDescription = paint.BodyPaintDescription;
          roofOption.RoofPaintSwatchHex = paint.BodyPaintSwatchHex;
          roofOption.RoofPaintSwatchImage = paint.BodyPaintSwatchImage;
        }

        var foundRoofPaint = $filter("arrayFind")(foundBodyPaint.RoofOptions, function (foundBodyPaintRoofOption) {
          return foundRoofPaintInFoundBodyPaintRoofOption(foundBodyPaintRoofOption, roofOption, paint);
        });

        if (!foundRoofPaint) {
          foundBodyPaint.RoofOptions.push(roofOption);
          cache.roofPaints.push(roofOption);
          cache.roofPaints = cache.roofPaints.sort(function (a, b) {
            return a.RoofPaintOrder - b.RoofPaintOrder;
          });
        }
      }

      function getPaintsByVariantId(variantId, optionalPricingParams, optionalTrimIds) {
        var pricingParamsOrUndefined;

        if (optionalPricingParams && optionalPricingParams.postcode) {
          // get pricing only if postcode valid
          pricingParamsOrUndefined = optionalPricingParams;
        }

        return getTrimsByVariantId(variantId).then(function (trims) {
          var filteredTrims = trims;

          if (optionalTrimIds) {
            filteredTrims = filteredTrims.filter(function (trim) {
              return optionalTrimIds.find(function (trimId) {
                return trim.ID === trimId;
              });
            });
          }

          var trimPromises = filteredTrims.map(function (trim) {
            return getPaintsByTrimId(trim.ID, pricingParamsOrUndefined).then(function (paints) {
              return paints;
            });
          });

          return API.all(trimPromises).then(function (paintsByTrim) {
            var allTrimPaints = paintsByTrim.reduce(function (acc, paints) {
              return acc.concat(paints);
            }, []);

            allTrimPaints.forEach(function (paint, index) {
              hydrateThenCacheItem(paint, "paint", pricingParamsOrUndefined);
              allTrimPaints[index] = cache.range.paint[paint.ID];
            });

            return allTrimPaints;
          });
        });
      }

      function getPaintsByGradeId(gradeId, pricingParams) {
        return getChildrenByParentId(gradeId, "variant", "grade", null, pricingParams).then(function (
          variantsByGradeId
        ) {
          var paintsByVariantIdPromises = variantsByGradeId.map(function (variant) {
            return getPaintsByVariantId(variant.ID, pricingParams);
          });

          return API.all(paintsByVariantIdPromises).then(function (paintsByVariantId) {
            return paintsByVariantId.reduce(function (deferredResult, value) {
              return deferredResult.concat(value);
            }, []);
          });
        });
      }

      function getPaintsGroupedByVariantIds(variantIds, pricingParams) {
        var paintsByVariantIdPromises = variantIds.map(function (variantId) {
          return getPaintsByVariantId(variantId, pricingParams).then(function (paintsByVariantId) {
            return { variantId: variantId, paints: paintsByVariantId }; // an envelope
          });
        });

        return API.all(paintsByVariantIdPromises).then(function (envelopes) {
          return envelopes.reduce(function (deferredResult, envelope) {
            deferredResult[envelope.variantId] = envelope.paints;
            return deferredResult;
          }, []);
        });
      }

      function getPaintsByVariantIds(variantIds, optionalPricingParams, optionalTrimIds) {
        var paintsByVariantIdPromises = variantIds.map(function (variantId) {
          return getPaintsByVariantId(variantId, optionalPricingParams, optionalTrimIds);
        });

        return API.all(paintsByVariantIdPromises).then(function (paintsByVariantId) {
          return paintsByVariantId.reduce(function (deferredResult, value) {
            return deferredResult.concat(value);
          }, []);
        });
      }

      /*
       * Filter variantIds down to just the variants matching the name of the first one,
       * including any with the " Two Tone" suffix
       */

      function filterVariantIdsByFirstVariant(variantIds) {
        if (!variantIds.length) {
          throw new Error(
            "filterVariantIdsByFirstVariant requires an array containing at least one variantId"
          );
        }

        return getVariantById(variantIds[0]).then(function (variant) {
          return filterVariantIdsByVariant(variant, variantIds);
        });
      }

      function filterVariantIdsByVariant(filterVariant, variantIds) {
        return getVariantsByIds(variantIds).then(function (variants) {
          var allVariants = variants.map(function (variant) {
            return angular.extend(angular.copy(variant), {
              name: variant.name,
              cabChassis: variant.cabChassis,
              drivetrain: variant.drivetrain,
              engineType: variant.engineType,
              seats: variant.seats,
              optionPack: variant.optionPack,
            });
          });

          var matchingVariants = allVariants.filter(function (variant) {
            return variant.cabChassis === filterVariant.cabChassis &&
              variant.drivetrain === filterVariant.drivetrain &&
              variant.engineType === filterVariant.engineType &&
              variant.seats === filterVariant.seats &&
              variant.optionPack === filterVariant.optionPack;
          });

          return matchingVariants.map(function (variant) {
            return variant.ID;
          });
        });
      }

      function getUniquePaintsByVariantIds(variantIds, optionalPricingParams, optionalTrimIds) {
        return getPaintsByVariantIds(variantIds, optionalPricingParams, optionalTrimIds).then(function (paints) {
          return paints.reduce(function (uniquePaints, value) {
            var isUniquePaint = !uniquePaints.find(function (resultPaint) {
              return resultPaint.BodyPaintDescription === value.BodyPaintDescription;
            });

            if (isUniquePaint) {
              return uniquePaints.concat(value);
            }

            return uniquePaints;
          }, []);
        });
      }

      function getPaintsForPaintSelector(variantIds, optionalPricingParams, trimIds) {
        return filterVariantIdsByFirstVariant(variantIds).then(function (filteredVariantIds) {
          return getUniquePaintsByVariantIds(filteredVariantIds, optionalPricingParams, trimIds);
        });
      }

      function getUniqueTrimsByVariantIds(variantIds, pricingParams) {
        return getTrimsByVariantIds(variantIds, pricingParams).then(function (trims) {
          return trims.reduce(function (uniqueTrims, value) {
            var isUniqueTrim = !uniqueTrims.find(function (resultTrim) {
              return resultTrim.TrimCode === value.TrimCode;
            });

            if (isUniqueTrim) {
              return uniqueTrims.concat(value);
            }

            return uniqueTrims;
          }, []);
        });
      }

      function getTrimGroupsByVariantIds(variantIds, pricingParams) {
        return getTrimsByVariantIds(variantIds, pricingParams).then(function (trims) {
          /*
           * 1: Get list of all unique TrimCodes
           */
          var uniqueTrimCodes = trims.reduce(function (trimCodes, trim) {
            var isUniqueTrimCode = !trimCodes.find(function (trimCode) {
              return trimCode === trim.TrimCode;
            });

            if (isUniqueTrimCode) {
              return trimCodes.concat(trim.TrimCode);
            }

            return trimCodes;
          }, []);

          var trimGroups = [];

          /*
           * 2: Aggregate trims by TrimCode into "representative" trims, where properties are kept only if they
           * have the same value for every trim in the group
           */
          angular.forEach(uniqueTrimCodes, function (uniqueTrimCode) {
            var trimsInGroup = trims.filter(function (trim) {
              return trim.TrimCode === uniqueTrimCode;
            });

            var representativeTrim = trims.reduce(function (aggregateTrim, trimInGroup) {
              var accTrim = angular.copy(aggregateTrim);

              angular.forEach(accTrim, function (accTrimPropValue, accTrimPropKey) {
                if (accTrim[accTrimPropKey] !== trimInGroup[accTrimPropKey]) {
                  accTrim[accTrimPropKey] = "";
                }
              });

              return aggregateTrim;
            }, trimsInGroup[0]);

            var trimGroup = angular.extend({}, representativeTrim, {
              trims: trimsInGroup,
            });

            trimGroups.push(trimGroup);
          });

          return trimGroups;
        });
      }

      function getBodyPaintsWithRoofOptionsByPaintIdsFromCache(paintIds) {
        return cache.bodyPaints.filter(function (bodyPaint) {
          var cachedPaintIds = $filter("arrayIntersect")(paintIds, Object.keys(cache.range.paint));
          return cachedPaintIds.find(function (cachedPaintId) {
            var paint = cache.range.paint[cachedPaintId];
            return foundBodyPaintForPaint(bodyPaint, paint);
          });
        });
      }

      function getGradesList() {
        return cache.gradesList;
      }

      function getVariantById(id) {
        return getItemWithChildrenById(id, "variant", "trim", RangeVariantApi);
      }

      function getVariantsByIds(ids) {
        return getItemsWithChildrenByIds(ids, "variant", "trim", RangeVariantApi);
      }

      function getVariantIdByTrimId(id) {
        return getParentIdByChildId(id, "variant");
      }

      function setVariantOptionsInCache(variantOptions) {
        cache.variantOptions = variantOptions;
      }

      function getVariantOptionsFromCache() {
        return cache.variantOptions;
      }

      function getTrimById(id, pricingParams) {
        return getItemWithChildrenById(id, "trim", "paint", RangeTrimApi, pricingParams);
      }

      function getTrimByIdFromCache(trimId) {
        return cache.range.trim[trimId];
      }

      function getTrimIdByPaintId(id) {
        return getParentIdByChildId(id, "trim");
      }

      function getTrimsByVariantId(id, optionalPricingParams) {
        return getChildrenByParentId(id, "trim", "variant", RangeVariantApi, optionalPricingParams);
      }

      function getTrimsByVariantIds(ids, optionalPricingParams) {
        return getChildrenByParentIds(ids, "trim", "variant", RangeVariantApi, optionalPricingParams);
      }

      function getPaintByMaterialCode(materialCode) {
        return getPaintByMaterialCodeEx(materialCode, {}, true);
      }

      function getPaintById(id, pricingParams) {
        return getItemWithChildrenById(id, "paint", null, RangePaintApi, pricingParams);
      }

      function getPaintsByTrimId(id, optionalPricingParams) {
        return getChildrenByParentId(id, "paint", "trim", RangeTrimApi, optionalPricingParams);
      }

      function getBodyPaintByIdFromCache(bodyPaintId) {
        return cache.bodyPaints.find(function (bodyPaint) {
          return bodyPaint.BodyPaintId === bodyPaintId;
        });
      }

      function getRoofPaintByIdFromCache(roofPaintId) {
        return cache.roofPaints.find(function (roofPaint) {
          return roofPaint.ID === roofPaintId;
        });
      }

      function getCache() {
        return angular.copy(cache);
      }

      return {
        getGradeIdByMaterialCode: getGradeIdByMaterialCode,
        getGradeByIdFromCache: getGradeByIdFromCache,
        getGradesList: getGradesList,
        ingestGradesList: ingestGradesList,

        getVariantByMaterialCode: getVariantByMaterialCode,
        getVariantById: getVariantById,
        getVariantsByIds: getVariantsByIds,
        getVariantsByGradeIdFromCache: getVariantsByGradeIdFromCache,
        getVariantByIdFromCache: getVariantByIdFromCache,
        getVariantIdByTrimId: getVariantIdByTrimId,
        setVariantOptionsInCache: setVariantOptionsInCache,
        getVariantOptionsFromCache: getVariantOptionsFromCache,

        getTrimByMaterialCode: getTrimByMaterialCode,
        getTrimById: getTrimById,
        getTrimByIdFromCache: getTrimByIdFromCache,
        getTrimIdByPaintId: getTrimIdByPaintId,
        getTrimsByVariantId: getTrimsByVariantId,
        getTrimsByVariantIds: getTrimsByVariantIds,

        filterVariantIdsByFirstVariant: filterVariantIdsByFirstVariant,
        filterVariantIdsByVariant: filterVariantIdsByVariant,
        getUniquePaintsByVariantIds: getUniquePaintsByVariantIds,
        getUniqueTrimsByVariantIds: getUniqueTrimsByVariantIds,
        getPaintsForPaintSelector: getPaintsForPaintSelector,
        getTrimGroupsByVariantIds: getTrimGroupsByVariantIds,
        getPaintByMaterialCode: getPaintByMaterialCode,
        getPaintById: getPaintById,
        getPaintsByTrimId: getPaintsByTrimId,
        getPaintsByVariantId: getPaintsByVariantId,
        getPaintsByGradeId: getPaintsByGradeId,
        getPaintsGroupedByVariantIds: getPaintsGroupedByVariantIds,
        getPaintsByVariantIds: getPaintsByVariantIds,
        getBodyPaintsWithRoofOptionsByPaintIdsFromCache: getBodyPaintsWithRoofOptionsByPaintIdsFromCache,
        getBodyPaintByIdFromCache: getBodyPaintByIdFromCache,
        getRoofPaintByIdFromCache: getRoofPaintByIdFromCache,

        getPricingOptionsAndSpecialOffersByMaterialCode: getPricingOptionsAndSpecialOffersByMaterialCode,
        getPricingOptionsByMaterialCodes: getPricingOptionsByMaterialCodes,
        getPricingOptionsByMaterialCode: getPricingOptionsByMaterialCode,

        getCache: getCache,
      };
    },
  ]);
})(window.angular);
